import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import axios from 'axios';  // For axios.isCancel check
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Edit, Calendar, TrendingUp, DollarSign, Target, Activity, RefreshCw, Trash2, Home, Settings as SettingsIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import { useBranch } from '../context/BranchContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || API;

// Encon brand colors
const enconOrange = '#ee7203';
const enconBlack = '#1d1d1b';
const enconGrey = '#9d9d9c';
const enconLightGrey = '#ededed';

const SalesTracker = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [entries, setEntries] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [uploadDate, setUploadDate] = useState(() => {
    // Default to previous working day (excluding weekends)
    const today = new Date();
    const dayOfWeek = today.getDay();
    let daysToSubtract = 1;
    
    // If today is Monday (1), go back to Friday (3 days)
    if (dayOfWeek === 1) {
      daysToSubtract = 3;
    }
    // If today is Sunday (0), go back to Friday (2 days)
    else if (dayOfWeek === 0) {
      daysToSubtract = 2;
    }
    
    const previousWorkingDay = new Date();
    previousWorkingDay.setDate(previousWorkingDay.getDate() - daysToSubtract);
    return previousWorkingDay.toISOString().split('T')[0];
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [dateChangeConfirmed, setDateChangeConfirmed] = useState(false);
  const [carryOver, setCarryOver] = useState(0);
  const [editingCarryOver, setEditingCarryOver] = useState(false);
  const [editingContribution, setEditingContribution] = useState(null);
  const [contributionValue, setContributionValue] = useState('');
  const [weightModalOpen, setWeightModalOpen] = useState(false);
  const [weights, setWeights] = useState([]);
  const [editingWeight, setEditingWeight] = useState(null);
  const [weightValue, setWeightValue] = useState('');
  const [carryOverModalOpen, setCarryOverModalOpen] = useState(false);
  const [carryOverInput, setCarryOverInput] = useState('');
  const [lastUploadTime, setLastUploadTime] = useState(null);
  const [manualEntryModalOpen, setManualEntryModalOpen] = useState(false);
  const [editableEntries, setEditableEntries] = useState([]);
  const [savingEntries, setSavingEntries] = useState(false);
  const [fridayForecasts, setFridayForecasts] = useState([]);
  const [workingDays, setWorkingDays] = useState(20);
  const [workingDaysModalOpen, setWorkingDaysModalOpen] = useState(false);
  const [workingDaysInput, setWorkingDaysInput] = useState('');
  const [monthStartDate, setMonthStartDate] = useState(1); // Day of month to start from (default 1st)
  const [monthStartDateInput, setMonthStartDateInput] = useState('1');
  // Tail Forecast (last-few-days handling): "decline" (default, hardcoded 0.50/0.25/0),
  // "average" (tail days use the normal multiplier, only the true last day stays £0),
  // "manual" (Jamie's own £ figures for however many of the last days). Saved per month
  // alongside working_days/month_start_date via the same update-working-days endpoint.
  const [tailMode, setTailMode] = useState('decline');
  const [tailModeInput, setTailModeInput] = useState('decline');
  const [tailDailyAverage, setTailDailyAverage] = useState(null); // saved flat £ per day for the tail in "average" mode (null = use the current average)
  const [tailDailyAverageInput, setTailDailyAverageInput] = useState('');
  const [tailOverrides, setTailOverrides] = useState({}); // { "1": 4000, "2": 6000, ... } keyed by days-remaining
  const [tailOverrideCount, setTailOverrideCount] = useState(3); // how many of the last days to show inputs for
  const [forecastSummary, setForecastSummary] = useState(null); // New forecast summary from performance-based system
  const [forecastModalTab, setForecastModalTab] = useState('daily'); // 'daily' or 'breakdown'
  
  // Get branch context for filtering
  const { getEffectiveBranchId } = useBranch();
  const effectiveBranchId = getEffectiveBranchId();

  useEffect(() => {
    loadFinancialYears();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      loadMonths();
    }
  }, [selectedYear, effectiveBranchId]);

  useEffect(() => {
    let abortController = new AbortController();
    
    if (selectedYear && selectedMonth) {
      // Clear old entries immediately when month changes
      setEntries([]);
      setLoadingEntries(true); // Show loading state
      setSummary({ 
        current_total_sales: 0, 
        auto_forecast_eom: 0, 
        actual_sales: 0,
        carryover: 0,
        needs_carryover: false
      });
      setCarryOver(0); // Clear carryover too
      setWorkingDays(20); // Reset to default
      setForecastSummary(null); // Reset forecast summary
      
      loadEntries(abortController.signal);
      loadCarryOver();
      loadWorkingDays();
      loadWeights(); // Load forecast data
    }
    
    // Cleanup: abort pending requests when month/year changes
    return () => {
      abortController.abort();
    };
  }, [selectedYear, selectedMonth, effectiveBranchId]);

  const loadCarryOver = async () => {
    try {
      const response = await api.get(`${API}/sales-tracker/month-carryover`, {
        params: { year_id: selectedYear.id, month_number: selectedMonth.month_number }
      });
      setCarryOver(response.data.carryover || 0);
    } catch (error) {
      console.error('Error loading carry over:', error);
    }
  };

  const saveCarryOver = async (value) => {
    try {
      await api.put(`${API}/sales-tracker/month-carryover`, null, {
        params: {
          year_id: selectedYear.id,
          month_number: selectedMonth.month_number,
          carryover: value
        }
      });
      setCarryOver(value);
      setEditingCarryOver(false);
      toast.success('Carry over saved');
    } catch (error) {
      toast.error('Failed to save carry over');
    }
  };

  const loadWorkingDays = async () => {
    if (!selectedYear || !selectedMonth) return;
    try {
      const response = await api.get(`${API}/sales-tracker/month-metadata`, {
        params: { year_id: selectedYear.id, month_number: selectedMonth.month_number }
      });
      setWorkingDays(response.data.working_days || 20);
      setMonthStartDate(response.data.month_start_date || 1);
      setTailMode(response.data.tail_mode || 'decline');
      setTailOverrides(response.data.tail_manual_overrides || {});
      setTailDailyAverage(response.data.tail_daily_average ?? null);
    } catch (error) {
      console.error('Error loading working days:', error);
      // Set defaults if error
      setWorkingDays(20);
      setMonthStartDate(1);
      setTailMode('decline');
      setTailOverrides({});
      setTailDailyAverage(null);
    }
  };

  const saveWorkingDays = async (workingDaysValue, startDateValue, tailModeValue, tailOverridesValue, tailDailyAverageValue = null) => {
    try {
      await api.put(`${API}/sales-tracker/update-working-days`, null, {
        params: {
          year_id: selectedYear.id,
          month_number: selectedMonth.month_number,
          working_days: workingDaysValue,
          month_start_date: startDateValue,
          tail_mode: tailModeValue,
          tail_manual_overrides: tailModeValue === 'manual' ? JSON.stringify(tailOverridesValue || {}) : '',
          // Only sent in "average" mode; leaving it blank clears it so the month's current average is used
          ...(tailModeValue === 'average' && tailDailyAverageValue !== null && tailDailyAverageValue !== undefined ? { tail_daily_average: tailDailyAverageValue } : {})
        }
      });
      setWorkingDays(workingDaysValue);
      setMonthStartDate(startDateValue);
      setTailMode(tailModeValue);
      setTailOverrides(tailModeValue === 'manual' ? (tailOverridesValue || {}) : {});
      setTailDailyAverage(tailModeValue === 'average' ? (tailDailyAverageValue ?? null) : null);
      setWorkingDaysModalOpen(false);
      toast.success('Settings saved');
      // Refresh entries to update calculations
      await loadEntries();
      await loadWeights(); // Refresh forecast with new start date
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const updateContribution = async (entryId, value) => {
    try {
      await api.put(`${API}/sales-tracker/entry/${entryId}/contribution`, null, {
        params: { contribution_percent: value }
      });
      // Refresh entries
      await loadEntries();
      setEditingContribution(null);
      toast.success('Contribution updated');
    } catch (error) {
      toast.error('Failed to update contribution');
    }
  };

  const loadWeights = async () => {
    if (!selectedYear || !selectedMonth) return;
    try {
      const response = await api.get(
        `${API}/sales-tracker/forecast/${selectedYear.id}/${selectedMonth.month_number}`,
        { }
      );
      // New endpoint returns { summary: {...}, daily_breakdown: [...] }
      setWeights(response.data.daily_breakdown || []);
      setForecastSummary(response.data.summary || null);
    } catch (error) {
      console.error('Error loading forecast:', error);
      toast.error('Failed to load forecast data');
    }
  };

  const updateWeight = async (entryId, value) => {
    try {
      await api.put(`${API}/sales-tracker/entry/${entryId}/weight`, null, {
        params: { weight: value }
      });
      await loadWeights();
      await loadEntries(); // Refresh main entries to update forecast
      setEditingWeight(null);
      toast.success('Weight updated - forecast recalculated');
    } catch (error) {
      toast.error('Failed to update weight');
    }
  };

  const handleSaveCarryOverFromModal = async () => {
    const value = parseFloat(carryOverInput) || 0;
    await saveCarryOver(value);
    setCarryOverModalOpen(false);
    await loadEntries(); // Refresh to update calculations
  };

  const loadFinancialYears = async () => {
    try {
      const response = await api.get(`${API}/financial-years`);
      const years = response.data.financial_years || [];
      setFinancialYears(years);
      
      const currentYear = years.find(y => y.is_current) || years[0];
      if (currentYear) {
        setSelectedYear(currentYear);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
      toast.error('Failed to load financial years');
    }
  };

  const loadMonths = async () => {
    try {
      const response = await api.get(`${API}/sales-tracker/months`, {
        params: { year_id: selectedYear.id }
      });
      setMonths(response.data.months || []);
      
      // Auto-select current month
      const currentMonth = new Date().getMonth() + 1;
      const currentMonthData = response.data.months.find(m => m.month_number === currentMonth);
      if (currentMonthData) {
        setSelectedMonth(currentMonthData);
        // Store selected month in localStorage for SalesInfoBar
        localStorage.setItem('salesTracker_yearId', response.data.year.id);
        localStorage.setItem('salesTracker_monthNumber', currentMonthData.month_number.toString());
      } else {
        setSelectedMonth(response.data.months[0]);
        if (response.data.months[0]) {
          localStorage.setItem('salesTracker_yearId', response.data.year.id);
          localStorage.setItem('salesTracker_monthNumber', response.data.months[0].month_number.toString());
        }
      }
    } catch (error) {
      toast.error('Failed to load months: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const [summary, setSummary] = useState({ 
    current_total_sales: 0, 
    remaining_weighted_forecast: 0, 
    auto_forecast_eom: 0, 
    actual_sales: 0,
    carryover: 0 
  });
  const [loadingEntries, setLoadingEntries] = useState(false);
  const currentMonthRef = useRef(null);

  const loadEntries = async (signal = null) => {
    if (!selectedYear || !selectedMonth) return;
    
    // Track which month we're loading
    const monthKey = `${selectedYear.id}_${selectedMonth.month_number}`;
    currentMonthRef.current = monthKey;
    
    setLoadingEntries(true);
    try {
      const config = signal ? { signal } : { };
      const response = await api.get(
        `${API}/sales-tracker/entries/${selectedYear.id}/${selectedMonth.month_number}`,
        config
      );
      
      // Check if request was aborted before updating state
      if (signal && signal.aborted) {
        return;
      }
      
      // Only update if we're still on the same month (prevent race condition)
      if (currentMonthRef.current !== monthKey) {
        console.log('⚠️ Discarding stale data from previous month');
        return;
      }
      
      setEntries(response.data.entries || []);
      setSummary(response.data.summary || { 
        current_total_sales: 0, 
        auto_forecast_eom: 0, 
        actual_sales: 0,
        carryover: 0,
        needs_carryover: false
      });
      
      // If carry-over needs to be set, show modal automatically
      if (response.data.summary?.needs_carryover) {
        setCarryOverModalOpen(true);
        toast.info('⚠️ Please set the carry-over amount for this month', { duration: 5000 });
      }
      
      // Find last upload time
      const entriesWithPhotos = response.data.entries?.filter(e => e.has_photo || e.is_manual_entry) || [];
      if (entriesWithPhotos.length > 0) {
        const lastEntry = entriesWithPhotos[entriesWithPhotos.length - 1];
        setLastUploadTime(lastEntry.updated_at || lastEntry.created_at);
      }
      
      if (response.data.entries?.length > 0) {
        toast.success(`Loaded ${response.data.entries.length} entries`);
      }
      
      // Load Friday forecasts
      await loadFridayForecasts();
    } catch (error) {
      // Ignore abort errors
      if (axios.isCancel(error) || error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        console.log('Request cancelled - month changed');
        return;
      }
      toast.error('Failed to load tracker entries: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingEntries(false);
    }
  };

  const handleDateChange = (newDate) => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];
    
    // If date is different from yesterday and not confirmed, ask for confirmation
    if (newDate !== yesterdayStr && !dateChangeConfirmed) {
      if (window.confirm(`Are you sure you want to upload data for ${new Date(newDate).toLocaleDateString()}? The default is yesterday's date.`)) {
        setUploadDate(newDate);
        setDateChangeConfirmed(true);
      }
    } else {
      setUploadDate(newDate);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(
        `${API}/sales-tracker/upload-photo`,
        formData,
        {
          params: { year_id: selectedYear.id, date: uploadDate },
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      toast.success('Photo uploaded and analyzed successfully!');
      setUploadModalOpen(false);
      setDateChangeConfirmed(false); // Reset confirmation
      
      // Reset to yesterday for next upload
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      setUploadDate(yesterday.toISOString().split('T')[0]);
      
      // Refresh all data and calculations
      await loadEntries();
      await loadWeights();
      
      // Update last upload time
      setLastUploadTime(new Date().toISOString());
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const saveAllEntries = async () => {
    setSavingEntries(true);
    console.log('🔵 Starting save operation...');
    console.log('🔵 Total entries:', editableEntries.length);
    
    try {
      let successCount = 0;
      let errorCount = 0;
      let failedEntries = [];

      // Save ALL entries (simplified - no modification tracking)
      for (const entry of editableEntries) {
        // Skip entries without IDs
        if (!entry.id) {
          console.warn('Skipping entry without ID:', entry.date, 'WD:', entry.working_day);
          failedEntries.push({ date: entry.date, wd: entry.working_day, error: 'Missing ID' });
          errorCount++;
          continue;
        }
        
        try {
          console.log('Saving entry:', entry.id, 'Date:', entry.date, 'WD:', entry.working_day);
          console.log('Data:', {
            invoiced: parseFloat(entry.invoiced) || 0,
            daily_invoiced: parseFloat(entry.daily_invoiced) || 0,
            outstanding: parseFloat(entry.outstanding) || 0,
            forecasted_sales: parseFloat(entry.forecasted_sales || entry.forecasted_sales_value) || 0,
            contribution_percentage: parseFloat(entry.contribution_percentage) || 0
          });
          
          const saveData = {
            invoiced: parseFloat(entry.invoiced) || 0,
            daily_invoiced: parseFloat(entry.daily_invoiced) || 0,
            outstanding: parseFloat(entry.outstanding) || 0,
            forecasted_sales: parseFloat(entry.forecasted_sales || entry.forecasted_sales_value) || 0,
            contribution_percentage: parseFloat(entry.contribution_percentage) || 0,
            // CRITICAL: Include year_id and month_number to ensure correct entry is updated
            year_id: selectedYear.id,
            financial_year_id: selectedYear.id,
            month_number: selectedMonth.month_number
          };
          
          console.log(`📤 Sending PUT to: ${BACKEND_URL}/api/sales-tracker/entry/${entry.id}`);
          console.log('📦 Payload:', saveData);
          
          const response = await api.put(
            `${BACKEND_URL}/api/sales-tracker/entry/${entry.id}`,
            saveData,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
          
          console.log('📥 Response:', response.data);
          console.log('✅ Saved entry:', entry.date, entry.id);
          successCount++;
        } catch (error) {
          console.error('❌ Error saving entry:', entry.date, 'WD:', entry.working_day);
          console.error('Error details:', error.response?.data || error.message);
          failedEntries.push({ date: entry.date, wd: entry.working_day, error: error.response?.data?.detail || error.message });
          errorCount++;
        }
      }

      console.log(`🔵 Save complete: ${successCount} success, ${errorCount} errors`);
      
      if (errorCount === 0) {
        toast.success(`✅ Saved ${successCount} entries! Page will reload in 2 seconds...`);
        setManualEntryModalOpen(false);
        
        // Force complete page reload to show changes
        setTimeout(() => {
          console.log('🔵 Reloading page now...');
          window.location.reload();
        }, 2000);
      } else {
        console.error('Failed entries:', failedEntries);
        const failedDates = failedEntries.map(e => `Day ${e.wd} (${e.date}): ${e.error}`).join(', ');
        toast.error(`Saved ${successCount} entries, ${errorCount} failed: ${failedDates.substring(0, 100)}`);
      }
    } catch (error) {
      console.error('Save all error:', error);
      toast.error('Failed to save entries');
    } finally {
      setSavingEntries(false);
    }
  };

  const clearEntryData = async (entryId) => {
    if (!window.confirm('Are you sure you want to clear all data for this day? This will reset all figures to 0.')) {
      return;
    }
    
    try {
      // Clear all editable fields by setting them to 0
      await api.put(
        `${BACKEND_URL}/api/sales-tracker/entry/${entryId}`,
        { 
          invoiced: 0,
          daily_invoiced: 0,
          outstanding: 0,
          forecasted_sales: 0,
          contribution_percentage: 0
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      // Update local state
      setEditableEntries(prev => prev.map(e => 
        e.id === entryId ? { 
          ...e, 
          invoiced: 0,
          daily_invoiced: 0,
          outstanding: 0,
          forecasted_sales: 0,
          forecasted_sales_value: 0,
          contribution_percentage: 0
        } : e
      ));
      
      // Refresh main table
      await loadEntries();
      toast.success('Entry data cleared');
    } catch (error) {
      console.error('Clear error:', error);
      toast.error(error.response?.data?.detail || 'Failed to clear entry');
    }
  };

  const handlePasteImage = async (e) => {
    const items = e.clipboardData.items;
    for (let item of items) {
      if (item.type.indexOf('image') !== -1) {
        const blob = item.getAsFile();
        await handlePhotoUpload(blob);
        break;
      }
    }
  };

  const formatCurrency = (amount, showSymbol = true) => {
    if (!amount) return showSymbol ? '£0.00' : '0';
    const formatted = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
    return showSymbol ? formatted : formatted.replace('£', '');
  };

  const loadFridayForecasts = async () => {
    if (!selectedYear || !selectedMonth) return;
    
    try {
      const response = await api.get(
        `${API}/sales-tracker/friday-forecasts/${selectedYear.id}/${selectedMonth.month_number}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setFridayForecasts(response.data.forecasts || []);
    } catch (error) {
      console.error('Error loading Friday forecasts:', error);
    }
  };

  const getYesterdayData = () => {
    if (!entries.length) return null;
    
    // Find the last entry with actual data (non-zero daily_invoiced or days_sales)
    const lastDataIndex = entries
      .map((e, i) => {
        const hasInvoiced = (e.daily_invoiced || e.invoiced_yesterday || 0) > 0;
        const hasSales = (e.days_sales || 0) > 0;
        const hasTotalSales = (e.forecasted_sales || e.forecasted_sales_value || 0) > 0;
        return (hasInvoiced || hasSales || hasTotalSales) ? i : -1;
      })
      .filter(i => i >= 0)
      .pop();
    
    if (lastDataIndex === undefined || lastDataIndex < 0) return null;
    
    const latestEntry = entries[lastDataIndex];
    const yesterdayEntry = lastDataIndex > 0 ? entries[lastDataIndex - 1] : null;
    
    const daysLeft = (selectedMonth?.working_days || 0) - (latestEntry?.working_day || 0);
    
    // Get the date of the last entry
    const entryDate = latestEntry.date ? new Date(latestEntry.date) : null;
    let formattedDate = 'Last Entry';
    if (entryDate) {
      const dayName = entryDate.toLocaleDateString('en-GB', { weekday: 'long' });
      const day = entryDate.getDate();
      const ordinal = day === 1 || day === 21 || day === 31 ? 'st' 
                    : day === 2 || day === 22 ? 'nd'
                    : day === 3 || day === 23 ? 'rd'
                    : 'th';
      formattedDate = `${dayName} ${day}${ordinal}`;
    }
    
    // Handle both old and new field names for backward compatibility
    const daily_invoiced = latestEntry.daily_invoiced || latestEntry.invoiced_yesterday || 0;
    const total_sales = latestEntry.forecasted_sales || latestEntry.forecasted_sales_value || 0;
    
    // Calculate Order Intake from the last entry
    let order_intake = 0;
    if (lastDataIndex === 0) {
      const daily_sales = latestEntry.days_sales || 0;
      order_intake = carryOver - daily_sales;
    } else {
      const yesterday_total_sales = yesterdayEntry 
        ? (yesterdayEntry.forecasted_sales || yesterdayEntry.forecasted_sales_value || 0)
        : 0;
      order_intake = total_sales - yesterday_total_sales;
    }
    
    // Use unified forecast from forecastSummary if available
    const workingDaysCount = forecastSummary?.working_days || selectedMonth?.working_days || workingDays || 20;
    const budget = forecastSummary?.budget || selectedMonth?.budget || 0;
    
    // SPWD Budget = Budget / Working Days
    const spwd_budget = workingDaysCount > 0 ? budget / workingDaysCount : 0;
    
    // SPWD Forecast = Forecast EOM / Working Days (from unified calculation)
    const forecast_eom = forecastSummary?.forecast_eom || summary.auto_forecast_eom || 0;
    const spwd_forecast = forecastSummary?.spwd_forecast || (workingDaysCount > 0 ? forecast_eom / workingDaysCount : 0);
    
    return {
      yesterdays_invoiced: daily_invoiced,
      yesterdays_sales: order_intake,
      order_intake: order_intake,
      spwd_budget,
      spwd_forecast,
      forecast_eom,
      daysLeft,
      formattedDate,
      // Additional data from unified forecast
      average_daily_intake: forecastSummary?.average_daily_intake || 0,
      performance_vs_baseline: forecastSummary?.performance_vs_baseline || 100,
      variance_from_budget: forecastSummary?.variance_from_budget || 0,
      variance_percentage: forecastSummary?.variance_percentage || 0,
      forecast_status: forecastSummary?.forecast_status || 'on_track'
    };
  };

  const yesterdayData = getYesterdayData();

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen" style={{ backgroundColor: enconLightGrey }}>
      {/* Global Header with Sales Bar */}
      <AppHeader 
        user={user} 
        onLogout={onLogout}
        subHeaderRight={
          <>
            {/* Upload button — lives in the Global Sub-Header (right side), slim glass pill, Permission Gated.
                It carries the "bg-white" class so the sub-header glass styling picks it up; the colour follows
                the sub-header's own accent (var(--g-accent)), falling back to the Encon orange. */}
            {(user?.role === 'super_admin' || user?.can_upload_sales_tracker) && (
              <button
                type="button"
                onClick={() => setUploadModalOpen(true)}
                className="bg-white h-7 px-3 rounded-full flex items-center gap-1.5 text-xs font-semibold transition-all shrink-0"
                style={{ color: `var(--g-accent, ${enconOrange})` }}
                title="Upload M4 Sales Data"
                data-testid="sales-tracker-upload-btn"
              >
                <Upload className="w-3.5 h-3.5" />
                <span>Upload</span>
              </button>
            )}
          </>
        }
      />

      {/* Main Content */}
      <div className="px-4 lg:px-8 py-6 space-y-6" style={{ paddingTop: '88px' }}>

      {/* Month Selector - August to July - Full Width */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 w-full">
        <div className="flex items-center gap-4 w-full">
          {/* Previous Year Arrow */}
          <button
            onClick={() => {
              const currentIndex = financialYears.findIndex(y => y.id === selectedYear?.id);
              if (currentIndex > 0) {
                setSelectedYear(financialYears[currentIndex - 1]);
              }
            }}
            disabled={financialYears.findIndex(y => y.id === selectedYear?.id) === 0}
            className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex-shrink-0"
            style={{ color: enconOrange }}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Months: August to July - Full Width Grid */}
          <div className="flex-1 grid grid-cols-12 gap-2">
            {['August', 'September', 'October', 'November', 'December', 'January', 'February', 'March', 'April', 'May', 'June', 'July'].map((monthName, idx) => {
              const monthNum = idx < 5 ? idx + 8 : idx - 4; // Aug=8, Sep=9...Dec=12, Jan=1...Jul=7
              const month = months.find(m => m.month_number === monthNum);
              
              return (
                <button
                  key={monthNum}
                  onClick={() => {
                    if (month) {
                      setSelectedMonth(month);
                      // Store selected month in localStorage for SalesInfoBar
                      localStorage.setItem('salesTracker_yearId', selectedYear.id);
                      localStorage.setItem('salesTracker_monthNumber', month.month_number.toString());
                      loadEntries();
                    }
                  }}
                  className={`px-2 py-3 rounded-lg font-semibold text-sm transition-all ${
                    selectedMonth?.month_number === monthNum
                      ? 'text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={
                    selectedMonth?.month_number === monthNum
                      ? { backgroundColor: enconOrange }
                      : {}
                  }
                >
                  {monthName}
                </button>
              );
            })}
          </div>

          {/* Next Year Arrow */}
          <button
            onClick={() => {
              const currentIndex = financialYears.findIndex(y => y.id === selectedYear?.id);
              if (currentIndex < financialYears.length - 1) {
                setSelectedYear(financialYears[currentIndex + 1]);
              }
            }}
            disabled={financialYears.findIndex(y => y.id === selectedYear?.id) === financialYears.length - 1}
            className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex-shrink-0"
            style={{ color: enconOrange }}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Budget
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(selectedMonth?.budget || 0)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {selectedMonth?.working_days || 0} working days
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Forecast EOM
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {formatCurrency(forecastSummary?.forecast_eom || summary.auto_forecast_eom)}
            </div>
            <p className={`text-xs mt-1 ${
              forecastSummary?.forecast_status === 'on_track' ? 'text-emerald-600' :
              forecastSummary?.forecast_status === 'over_budget' ? 'text-green-600' :
              forecastSummary?.forecast_status === 'under_budget' ? 'text-amber-600' : 'text-gray-500'
            }`}>
              {forecastSummary?.forecast_status === 'on_track' ? '✓ On Track' :
               forecastSummary?.forecast_status === 'over_budget' ? '↑ Over Budget' :
               forecastSummary?.forecast_status === 'under_budget' ? '↓ Under Budget' : 'Performance-based'}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <Target className="h-4 w-4" />
              Actual Sales
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(summary.actual_sales)}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Remaining
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div>
                <p className="text-xs text-gray-600">vs Budget:</p>
                {(() => {
                  const remaining = (selectedMonth?.budget || 0) - summary.actual_sales;
                  if (remaining <= 0) {
                    return (
                      <div className="flex items-center gap-1">
                        <p className="text-lg font-bold text-green-600">
                          Above Budget: {formatCurrency(Math.abs(remaining))}
                        </p>
                        <span className="text-green-600 text-xl">✓</span>
                      </div>
                    );
                  }
                  return (
                    <p className="text-lg font-bold text-blue-600">
                      {formatCurrency(remaining)}
                    </p>
                  );
                })()}
              </div>
              <div>
                <p className="text-xs text-gray-600">vs Forecast:</p>
                {(() => {
                  const forecastEom = forecastSummary?.forecast_eom || summary.auto_forecast_eom;
                  const remaining = forecastEom - summary.actual_sales;
                  if (remaining <= 0) {
                    return (
                      <div className="flex items-center gap-1">
                        <p className="text-lg font-bold text-green-600">
                          Above Forecast: {formatCurrency(Math.abs(remaining))}
                        </p>
                        <span className="text-green-600 text-xl">✓</span>
                      </div>
                    );
                  }
                  return (
                    <p className="text-lg font-bold text-orange-600">
                      {formatCurrency(remaining)}
                    </p>
                  );
                })()}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Friday Forecasts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {fridayForecasts.length > 0 ? (
                fridayForecasts.map((friday, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <p className="text-xs text-gray-600">
                      {new Date(friday.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}:
                    </p>
                    <p className="text-sm font-bold text-blue-600">
                      {formatCurrency(friday.forecast_eom)}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-500">No Friday data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Last Entry Details */}
      {yesterdayData && (
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="py-4">
            <div className="flex justify-center items-start gap-12 px-8">
              {/* Last Entry Invoice */}
              <div className="text-center flex-1">
                <p className="text-base text-gray-600 mb-2">{yesterdayData.formattedDate} Invoice</p>
                <p className="text-3xl font-bold text-green-600">
                  {formatCurrency(yesterdayData.yesterdays_invoiced)}
                </p>
              </div>
              
              {/* Last Entry Intake - Glass button */}
              <div className="text-center flex-1">
                <p className="text-base text-gray-600 mb-2">{yesterdayData.formattedDate} Intake</p>
                {(() => {
                  // Calculate baseline: Budget / Working Days (NOT subtracting carry over)
                  const baseline = (selectedMonth?.budget || 0) / (selectedMonth?.working_days || 1);
                  
                  // Calculate difference from baseline
                  const difference = yesterdayData.yesterdays_sales - baseline;
                  
                  let bgColor = 'bg-red-500';
                  let textColor = 'text-white';
                  let shadowColor = 'shadow-red-500/50';
                  
                  // Green: Same as or above baseline
                  if (difference >= 0) {
                    bgColor = 'bg-green-500';
                    shadowColor = 'shadow-green-500/50';
                  } 
                  // Yellow: Up to £5000 under baseline (but not more than £5000 under)
                  else if (difference >= -5000) {
                    bgColor = 'bg-yellow-400';
                    textColor = 'text-gray-900';
                    shadowColor = 'shadow-yellow-400/50';
                  }
                  // Red: £5000 or more under baseline (already set as default)
                  
                  return (
                    <div className={`${bgColor} ${textColor} px-6 py-3 rounded-full font-bold text-2xl backdrop-blur-sm bg-opacity-90 shadow-lg ${shadowColor} border border-white/20 inline-block`}>
                      {formatCurrency(yesterdayData.yesterdays_sales, false)}
                    </div>
                  );
                })()}
              </div>
              
              {/* SPWD Budget */}
              <div className="text-center flex-1">
                <p className="text-base text-gray-600 mb-2">SPWD Budget</p>
                <p className="text-3xl font-bold text-blue-600">
                  {formatCurrency(yesterdayData.spwd_budget)}
                </p>
              </div>
              
              {/* SPWD Forecast */}
              <div className="text-center flex-1">
                <p className="text-base text-gray-600 mb-2">SPWD Forecast</p>
                <p className="text-3xl font-bold text-orange-600">
                  {formatCurrency(yesterdayData.spwd_forecast)}
                </p>
              </div>
              
              {/* Baseline/Day */}
              <div className="text-center flex-1">
                <p className="text-base text-gray-600 mb-2">Baseline/Day</p>
                <p className="text-3xl font-bold text-purple-600">
                  {formatCurrency((selectedMonth?.budget || 0) / (selectedMonth?.working_days || 1))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Tabs */}
      <Card className="bg-white">
        <CardContent className="pt-6">
          {/* Sticky Header Section with Orange Border (3 sides) */}
          <div 
            className="sticky bg-white"
            style={{
              top: '85px',
              zIndex: 40,
              borderTop: '3px solid #fb923c',
              borderLeft: '3px solid #fb923c',
              borderRight: '3px solid #fb923c',
              borderTopLeftRadius: '0.5rem',
              borderTopRightRadius: '0.5rem',
              padding: '1rem',
              paddingBottom: '0.5rem',
              marginBottom: '0',
            }}
          >
            {/* Action Bar */}
            <div className="bg-white py-3 px-4 rounded-lg mb-4 border border-gray-200 shadow-sm">
              <div className="flex justify-between items-center">
                <div className="flex gap-2">
                  <Button 
                    onClick={() => {
                      loadWeights();
                      setWeightModalOpen(true);
                    }} 
                    variant="outline"
                    size="sm"
                    className="bg-white"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Weighted Forecast
                  </Button>
                </div>
                
                {/* Centered Last Upload/Refresh */}
                <div className="absolute left-1/2 transform -translate-x-1/2 text-center">
                  <p className="text-xs text-gray-500 mb-1">Last Upload / Refresh</p>
                  <p className="text-base font-bold text-gray-800">
                    {lastUploadTime ? new Date(lastUploadTime).toLocaleString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'No uploads yet'}
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={() => {
                      setWorkingDaysInput(workingDays.toString());
                      setMonthStartDateInput(monthStartDate.toString());
                      setTailModeInput(tailMode || 'decline');
                      setTailDailyAverageInput(tailDailyAverage !== null && tailDailyAverage !== undefined ? String(tailDailyAverage) : '');
                      setWorkingDaysModalOpen(true);
                    }} 
                    variant="outline"
                    size="sm"
                    className="bg-white"
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Working Days: {workingDays} {monthStartDate > 1 && `(from ${monthStartDate}${monthStartDate === 2 ? 'nd' : monthStartDate === 3 ? 'rd' : 'th'})`}
                  </Button>
                  
                  <Button 
                    onClick={() => {
                      console.log('🔵 Opening Manual Entry modal');
                      console.log('🔵 Current entries count:', entries.length);
                      const validEntries = entries.filter(e => e.id);
                      if (validEntries.length < entries.length) {
                        console.warn(`Filtered out ${entries.length - validEntries.length} entries without IDs`);
                        toast.warning(`${entries.length - validEntries.length} entries missing IDs - skipped`);
                      }
                      console.log('🔵 Valid entries for editing:', validEntries.length);
                      setEditableEntries(validEntries);
                      setManualEntryModalOpen(true);
                    }} 
                    variant="outline"
                    size="sm"
                    className="bg-white"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Manual Entry
                  </Button>
                </div>
              </div>
            </div>

            {/* Table Header - Sticky */}
            {entries.length > 0 && (
              <div 
                className="sticky grid grid-cols-12 gap-2 px-4 py-3 bg-gray-100 rounded-lg font-semibold text-sm text-gray-700 shadow-sm"
                style={{
                  top: '190px',
                  zIndex: 30
                }}
              >
                <div className="col-span-1">WD</div>
                <div className="col-span-1">Date</div>
                <div className="col-span-2">Invoiced</div>
                <div className="col-span-2">Daily Invoiced</div>
                <div className="col-span-2">Outstanding</div>
                <div className="col-span-2">Total Sales</div>
                <div className="col-span-1 text-center">Order Intake</div>
                <div className="col-span-1 text-right">Contrib %</div>
              </div>
            )}
          </div>

          {/* Tab Content - Scrollable */}
          {months.map(month => (
            selectedMonth?.month_number === month.month_number && (
              <div key={month.month_number} className="mt-0">
                <div className="space-y-4">
                  {/* Daily Entries */}
                  {loadingEntries ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mb-4"></div>
                        <p className="text-gray-600 font-medium">Loading {month.month_name} data...</p>
                      </div>
                    </div>
                  ) : entries.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">No entries for this month yet.</p>
                      <Button 
                        className="mt-4"
                        onClick={() => setUploadModalOpen(true)}
                      >
                        Add First Entry
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Carry Over Button - Above Day 1 */}
                      <button
                        onClick={() => setCarryOverModalOpen(true)}
                        className="w-full grid grid-cols-12 gap-2 px-4 py-4 border-2 rounded-lg transition-all hover:shadow-md hover:scale-[1.01] cursor-pointer"
                        style={{ 
                          backgroundColor: enconLightGrey,
                          borderColor: enconOrange
                        }}
                      >
                        <div className="col-span-12 flex items-center justify-center">
                          <span className="text-base font-semibold text-gray-700 mr-3">Carryover:</span>
                          <span className="text-2xl font-bold" style={{ color: enconOrange }}>
                            {formatCurrency(carryOver, false)}
                          </span>
                        </div>
                      </button>
                      
                      {/* Table Rows - Normal order (day 1 at top, day 18 at bottom) */}
                      {entries.map((entry, idx) => {
                        // Handle both old and new field names for backward compatibility
                        const total_sales = entry.forecasted_sales || entry.forecasted_sales_value || 0;
                        const invoiced = entry.invoiced || entry.sales_value || 0;
                        const daily_invoiced = entry.daily_invoiced || entry.invoiced_yesterday || 0;
                        const outstanding = entry.outstanding || 0;
                        
                        // Calculate Order Intake
                        // Special case: First day of EVERY month = Carryover - That Day's Sales
                        // Other days: Today's Total Sales - Yesterday's Total Sales
                        const isFirstDay = idx === 0;
                        
                        let order_intake = 0;
                        if (total_sales > 0) {
                          if (isFirstDay) {
                            // Day 1 of any month: Carryover - Daily Sales
                            const daily_sales = entry.days_sales || 0;
                            order_intake = carryOver - daily_sales;
                          } else {
                            // All other days: Today's Total Sales - Yesterday's Total Sales
                            const yesterday_total_sales = idx > 0 
                              ? (entries[idx - 1].forecasted_sales || entries[idx - 1].forecasted_sales_value || 0)
                              : 0;
                            order_intake = total_sales - yesterday_total_sales;
                          }
                        }
                        
                        // Show all entries - weighted forecast system handles future days
                        
                        return (
                        <div 
                          key={`${selectedMonth?.month_number || ''}_${entry.id}`} 
                          className="grid grid-cols-12 gap-2 px-4 py-4 bg-white border rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="col-span-1 text-gray-600 text-base font-medium">
                            {entry.working_day}
                          </div>
                          <div className="col-span-1 font-semibold text-base flex items-center gap-1">
                            <span>{new Date(entry.date).getDate()}</span>
                            {(() => {
                              const dayOfWeek = new Date(entry.date).getDay();
                              if (dayOfWeek === 1) { // Monday
                                return <span className="text-xs font-bold text-orange-500">Mon</span>;
                              } else if (dayOfWeek === 5) { // Friday
                                return <span className="text-xs font-bold text-purple-500">Fri</span>;
                              }
                              return null;
                            })()}
                          </div>
                          <div className="col-span-2 font-bold text-blue-600 text-base">
                            {formatCurrency(invoiced)}
                          </div>
                          <div className="col-span-2 text-green-600 font-semibold text-base">
                            {formatCurrency(daily_invoiced)}
                          </div>
                          <div className="col-span-2 text-orange-600 font-semibold text-base">
                            {formatCurrency(outstanding)}
                          </div>
                          <div className="col-span-2 font-bold text-purple-600 text-base">
                            {formatCurrency(total_sales)}
                          </div>
                          <div className="col-span-1 flex items-center justify-center">
                            {(() => {
                              // Calculate baseline: Budget / Working Days (NOT subtracting carry over)
                              const budget = selectedMonth?.budget || 0;
                              const workingDays = selectedMonth?.working_days || 1;
                              const baseline = budget / workingDays;
                              
                              // Calculate difference from baseline
                              const difference = order_intake - baseline;
                              
                              let bgColor = 'bg-red-500';
                              let textColor = 'text-white';
                              let shadowColor = 'shadow-red-500/50';
                              
                              // Green: Same as or above baseline
                              if (difference >= 0) {
                                bgColor = 'bg-green-500';
                                shadowColor = 'shadow-green-500/50';
                              } 
                              // Yellow: Up to £5000 under baseline (but not more than £5000 under)
                              else if (difference >= -5000) {
                                bgColor = 'bg-yellow-400';
                                textColor = 'text-gray-900';
                                shadowColor = 'shadow-yellow-400/50';
                              }
                              // Red: £5000 or more under baseline (already set as default)
                              
                              return (
                                <div className={`${bgColor} ${textColor} px-4 py-2 rounded-full font-bold text-lg backdrop-blur-sm bg-opacity-90 shadow-lg ${shadowColor} border border-white/20`}>
                                  {formatCurrency(order_intake, false)}
                                </div>
                              );
                            })()}
                          </div>
                          <div className="col-span-1 flex justify-end">
                            <div 
                              className="bg-orange-100 border-2 border-orange-400 rounded-lg px-3 py-1 text-gray-700 cursor-pointer hover:bg-orange-200 transition-colors text-right"
                              onClick={() => {
                                setEditingContribution(entry.id);
                                setContributionValue(entry.contribution_percent || '');
                              }}
                            >
                              {editingContribution === entry.id ? (
                                <Input
                                  type="number"
                                  value={contributionValue}
                                  onChange={(e) => setContributionValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      updateContribution(entry.id, parseFloat(contributionValue) || 0);
                                    } else if (e.key === 'Escape') {
                                      setEditingContribution(null);
                                    }
                                  }}
                                  onBlur={() => {
                                    if (contributionValue !== '') {
                                      updateContribution(entry.id, parseFloat(contributionValue) || 0);
                                    } else {
                                      setEditingContribution(null);
                                    }
                                  }}
                                  className="h-6 text-xs w-16"
                                  autoFocus
                                />
                              ) : (
                                <span className="font-semibold">{entry.contribution_percent ? `${entry.contribution_percent.toFixed(1)}%` : '-'}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                    </div>
                  )}
                </div>
              </div>
            )
          ))}
        </CardContent>
      </Card>

      {/* Upload Photo Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Daily Sales Data</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Date (defaults to previous working day)</Label>
              <Input
                type="date"
                value={uploadDate}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value + 'T00:00:00');
                  const dayOfWeek = selectedDate.getDay();
                  
                  // Block weekends (0 = Sunday, 6 = Saturday)
                  if (dayOfWeek === 0 || dayOfWeek === 6) {
                    toast.error('Weekends cannot be selected. Please choose a weekday.');
                    return;
                  }
                  
                  handleDateChange(e.target.value);
                }}
              />
              <p className="text-xs text-gray-500 mt-1">Weekends are not selectable. Changing the date will prompt for confirmation.</p>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-4">
                Upload or paste screenshot from Business Intelligence system
              </p>
              
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => handlePhotoUpload(e.target.files[0])}
                disabled={uploadingPhoto}
                className="mb-4"
              />
              
              <p className="text-sm text-gray-500 mb-2">Or paste image from clipboard:</p>
              <div
                contentEditable
                onPaste={handlePasteImage}
                className="border-2 border-blue-300 rounded-lg p-4 min-h-[100px] bg-blue-50 cursor-text"
              >
                <p className="text-blue-600 text-sm">Click here and paste (Ctrl+V / Cmd+V)</p>
              </div>
              
              {uploadingPhoto && (
                <div className="mt-4">
                  <div className="animate-pulse text-blue-600">
                    Analyzing image with AI...
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Modal */}
      <Dialog open={manualModalOpen} onOpenChange={setManualModalOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manual Entry</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Coming soon - manual data entry form</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Carry Over Modal */}
      <Dialog open={carryOverModalOpen} onOpenChange={setCarryOverModalOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Carry Over</DialogTitle>
            <p className="text-sm text-gray-600">
              Set the starting balance for {months.find(m => m.month_number === selectedMonth?.month_number)?.name || 'this month'}. This is automatically set when uploading day 1 data.
            </p>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Carry Over Amount (£)</Label>
              <Input
                type="number"
                placeholder={carryOver.toString()}
                value={carryOverInput}
                onChange={(e) => setCarryOverInput(e.target.value)}
                className="text-lg font-semibold"
                autoFocus
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setCarryOverModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveCarryOverFromModal} className="bg-emerald-600 hover:bg-emerald-700">
                Save Carry Over
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Working Days Modal */}
      <Dialog open={workingDaysModalOpen} onOpenChange={setWorkingDaysModalOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>Month Settings</DialogTitle>
            <p className="text-sm text-gray-600">
              Configure settings for {months.find(m => m.month_number === selectedMonth?.month_number)?.name || 'this month'}.
            </p>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Working Days */}
            <div>
              <Label className="text-base font-semibold">Total Working Days</Label>
              <Input
                type="number"
                placeholder={workingDays.toString()}
                value={workingDaysInput}
                onChange={(e) => setWorkingDaysInput(e.target.value)}
                className="text-lg font-semibold mt-2"
                autoFocus
                min="1"
                max="31"
              />
              <p className="text-xs text-gray-500 mt-1">
                Typically 20-23 working days per month (weekdays excluding bank holidays)
              </p>
            </div>
            
            {/* Month Start Date */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Month Start Date</Label>
              <p className="text-xs text-gray-500 mb-2">
                Set which day of the month sales tracking should start from. Dates before this will be excluded from calculations and forecasts.
              </p>
              <Input
                type="number"
                placeholder="1"
                value={monthStartDateInput}
                onChange={(e) => setMonthStartDateInput(e.target.value)}
                className="text-lg font-semibold"
                min="1"
                max="28"
              />
              <p className="text-xs text-gray-500 mt-1">
                Default is 1st. Set to e.g. 5 to start tracking from the 5th of each month.
              </p>
            </div>

            {/* Tail Forecast — how the last few working days of the month are forecast */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Tail Forecast</Label>
              <p className="text-xs text-gray-500 mb-2">
                How the last few working days of the month are forecast. The very last working day always shows £0 (no time left to invoice) — this only changes the days before it.
              </p>
              <div className="grid grid-cols-1 gap-2">
                <button
                  type="button"
                  onClick={() => setTailModeInput('decline')}
                  className={`text-left px-3 py-2 rounded-lg border-2 transition-all ${
                    tailModeInput === 'decline' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-semibold text-sm">Decline (default)</div>
                  <div className="text-xs text-gray-500">2nd-to-last day = 25% of average, 3rd-to-last = 50%, last day = £0</div>
                </button>
                <button
                  type="button"
                  onClick={() => setTailModeInput('average')}
                  className={`text-left px-3 py-2 rounded-lg border-2 transition-all ${
                    tailModeInput === 'average' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-semibold text-sm">Continue as average</div>
                  <div className="text-xs text-gray-500">The 2nd and 3rd-to-last days forecast at a daily average (set below, or this month's current average) — only the last day is £0</div>
                </button>
                <button
                  type="button"
                  onClick={() => setTailModeInput('manual')}
                  className={`text-left px-3 py-2 rounded-lg border-2 transition-all ${
                    tailModeInput === 'manual' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-semibold text-sm">Enter my own amounts</div>
                  <div className="text-xs text-gray-500">Type your own £ figure for however many of the last days you want</div>
                </button>
              </div>

              {tailModeInput === 'average' && (
                <div className="mt-3 pl-1 space-y-1" data-testid="tail-average-box">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-600 shrink-0">Daily average (£)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      placeholder={forecastSummary?.average_daily_intake ? String(Math.round(forecastSummary.average_daily_intake)) : 'Leave blank to use the current average'}
                      value={tailDailyAverageInput}
                      onChange={(e) => setTailDailyAverageInput(e.target.value)}
                      className="h-7 text-xs"
                      data-testid="tail-daily-average-input"
                    />
                  </div>
                  <p className="text-[11px] text-gray-500">
                    Used for the 2nd and 3rd-to-last working days. Leave blank to use this month's current average daily intake{forecastSummary?.average_daily_intake ? ` (${formatCurrency(forecastSummary.average_daily_intake)})` : ''}.
                  </p>
                </div>
              )}

              {tailModeInput === 'manual' && (
                <div className="mt-3 pl-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-600">Override last</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={tailOverrideCount}
                      onChange={(e) => setTailOverrideCount(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                      className="h-7 w-16 text-xs"
                    />
                    <Label className="text-xs text-gray-600">day(s)</Label>
                  </div>
                  {Array.from({ length: tailOverrideCount }, (_, i) => i + 1).map((daysRemaining) => {
                    const label = daysRemaining === 1 ? 'Last day' : daysRemaining === 2 ? '2nd-to-last day' : daysRemaining === 3 ? '3rd-to-last day' : `${daysRemaining}th-to-last day`;
                    return (
                      <div key={daysRemaining} className="flex items-center gap-2">
                        <Label className="text-xs text-gray-600 w-28 shrink-0">{label}</Label>
                        <Input
                          type="number"
                          placeholder="£0"
                          value={tailOverrides[String(daysRemaining)] ?? ''}
                          onChange={(e) => setTailOverrides(prev => ({ ...prev, [String(daysRemaining)]: e.target.value }))}
                          className="h-7 text-xs"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setWorkingDaysModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  const wdValue = parseInt(workingDaysInput);
                  const startValue = parseInt(monthStartDateInput) || 1;
                  if (wdValue && wdValue > 0 && wdValue <= 31 && startValue >= 1 && startValue <= 28) {
                    let overridesToSave = {};
                    if (tailModeInput === 'manual') {
                      for (const [k, v] of Object.entries(tailOverrides)) {
                        const num = parseFloat(v);
                        if (!isNaN(num)) overridesToSave[k] = num;
                      }
                    }
                    let averageToSave = null;
                    if (tailModeInput === 'average' && tailDailyAverageInput.trim() !== '') {
                      const parsedAverage = parseFloat(tailDailyAverageInput);
                      if (isNaN(parsedAverage) || parsedAverage < 0) {
                        toast.error('Enter a valid daily average (£0 or more), or leave it blank');
                        return;
                      }
                      averageToSave = parsedAverage;
                    }
                    saveWorkingDays(wdValue, startValue, tailModeInput, overridesToSave, averageToSave);
                  } else {
                    toast.error('Please enter valid values (Working days: 1-31, Start date: 1-28)');
                  }
                }} 
                className="bg-orange-600 hover:bg-orange-700"
              >
                Save Settings
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Weighted Forecast Modal */}
      <Dialog open={weightModalOpen} onOpenChange={setWeightModalOpen}>
        <DialogContent 
          className="bg-white w-[95vw] md:w-[85vw] lg:w-[80vw] xl:max-w-6xl overflow-y-auto shadow-2xl"
          style={{
            border: '4px solid transparent',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #10b981 0%, #059669 25%, #0891b2 75%, #06b6d4 100%)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            borderRadius: '1.5rem !important',
            position: 'fixed !important',
            top: '50% !important',
            left: '50% !important',
            transform: 'translate(-50%, -50%) !important',
            margin: '0 !important',
            maxHeight: '90vh !important',
            inset: 'auto !important',
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              Performance Forecast System
            </DialogTitle>
            <p className="text-sm text-gray-600">
              Forecast based on actual performance with end-of-month decline pattern
            </p>
          </DialogHeader>
          
          {/* Tabs */}
          <Tabs value={forecastModalTab} onValueChange={setForecastModalTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="daily" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
                📊 Daily Breakdown
              </TabsTrigger>
              <TabsTrigger value="breakdown" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
                📈 Calculation Summary
              </TabsTrigger>
            </TabsList>
            
            {/* Daily Breakdown Tab */}
            <TabsContent value="daily" className="space-y-4">
              {weights.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No entries for this month yet</p>
              ) : (
                <div className="space-y-2">
                  {/* Header */}
                  <div className="grid grid-cols-7 gap-3 px-4 py-2 bg-gray-100 rounded-lg font-semibold text-xs">
                    <div>Date</div>
                    <div>Day</div>
                    <div>Days Left</div>
                    <div>Multiplier</div>
                    <div>Actual Intake</div>
                    <div>Projected</div>
                    <div>Running Total</div>
                  </div>

                  {/* Rows */}
                  {weights.map((w, idx) => {
                    const hasActualData = w.has_actual_data || w.order_intake > 0;
                    const multiplier = w.day_multiplier || w.end_of_month_multiplier || 1;
                    const isHighMultiplier = multiplier > 1.2;
                    const isLowMultiplier = multiplier < 0.8;
                    
                    return (
                    <div 
                      key={w.id || idx} 
                      className={`grid grid-cols-7 gap-3 px-4 py-3 border rounded-lg text-sm ${
                        hasActualData ? 'bg-emerald-50 border-emerald-200' : w.is_manual_override ? 'bg-blue-50 border-blue-200' : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">
                        {new Date(w.date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}
                      </div>
                      <div className="text-gray-600">{w.day_name}</div>
                      <div className="text-gray-500">{w.days_remaining}</div>
                      <div className={`font-semibold ${
                        w.is_manual_override ? 'text-blue-600' :
                        isHighMultiplier ? 'text-emerald-600' : 
                        isLowMultiplier ? 'text-amber-600' : 'text-gray-700'
                      }`}>
                        {w.is_manual_override ? 'Manual' : w.is_tail_override ? 'Tail avg' : `${multiplier.toFixed(2)}×`}
                      </div>
                      <div className={`font-semibold ${hasActualData ? 'text-emerald-600' : 'text-gray-400'}`}>
                        {hasActualData ? formatCurrency(w.order_intake) : '—'}
                      </div>
                      <div className={`font-semibold ${hasActualData ? 'text-emerald-600' : w.is_manual_override ? 'text-blue-600' : 'text-blue-600'}`}>
                        {formatCurrency(w.projected_amount)}
                      </div>
                      <div className="font-bold text-indigo-600">
                        {formatCurrency(w.running_forecast)}
                      </div>
                    </div>
                    );
                  })}

                  {/* Totals Row */}
                  <div className="grid grid-cols-7 gap-3 px-4 py-3 bg-gradient-to-r from-emerald-100 to-cyan-100 rounded-lg font-bold text-sm border-2 border-emerald-300">
                    <div className="col-span-4 text-emerald-800">
                      TOTALS
                      {forecastSummary?.carryover_boost_percent > 0 && (
                        <span className="ml-2 text-xs font-normal text-emerald-600">
                          (+{forecastSummary.carryover_boost_percent}% carry over boost)
                        </span>
                      )}
                    </div>
                    <div className="text-emerald-600">
                      {formatCurrency(forecastSummary?.total_actuals || 0)}
                    </div>
                    <div className="text-blue-600">
                      {formatCurrency(forecastSummary?.base_forecast || ((forecastSummary?.total_actuals || 0) + (forecastSummary?.projected_remaining || 0)))}
                    </div>
                    <div className="text-indigo-700">
                      {formatCurrency(forecastSummary?.forecast_eom || 0)}
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
            
            {/* Calculation Summary Tab */}
            <TabsContent value="breakdown" className="space-y-4">
              {forecastSummary ? (
                <div className="space-y-4">
                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-100 p-4 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Budget</p>
                      <p className="text-xl font-bold text-gray-800">{formatCurrency(forecastSummary.budget)}</p>
                    </div>
                    <div className="bg-gray-100 p-4 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Baseline/Day</p>
                      <p className="text-xl font-bold text-gray-800">{formatCurrency(forecastSummary.baseline_per_day)}</p>
                    </div>
                    <div className="bg-gray-100 p-4 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Working Days</p>
                      <p className="text-xl font-bold text-gray-800">{forecastSummary.working_days}</p>
                    </div>
                    <div className={`p-4 rounded-lg ${
                      forecastSummary.carryover_below_threshold ? 'bg-amber-100' : 'bg-emerald-100'
                    }`}>
                      <p className="text-xs text-gray-600 mb-1">Carry Over</p>
                      <p className={`text-xl font-bold ${
                        forecastSummary.carryover_below_threshold ? 'text-amber-700' : 'text-emerald-700'
                      }`}>{formatCurrency(forecastSummary.carryover)}</p>
                      {forecastSummary.carryover_below_threshold && (
                        <p className="text-xs text-amber-600 mt-1">
                          Below £400k threshold
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Dynamic Adjustment Section */}
                  <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border-2 border-purple-200">
                    <h4 className="font-semibold text-lg mb-3 text-purple-800">Dynamic Multiplier Adjustment</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Performance Ratio</p>
                        <p className={`text-2xl font-bold ${
                          forecastSummary.performance_ratio >= 1.0 ? 'text-emerald-600' : 'text-amber-600'
                        }`}>{(forecastSummary.performance_ratio * 100).toFixed(1)}%</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {forecastSummary.performance_ratio >= 1.0 ? '≥ Baseline' : '< Baseline'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Adjustment Factor</p>
                        <p className={`text-2xl font-bold ${
                          forecastSummary.adjustment_factor >= 1.0 ? 'text-emerald-600' : 'text-amber-600'
                        }`}>{forecastSummary.adjustment_factor?.toFixed(2)}×</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {forecastSummary.adjustment_factor >= 1.0 ? 'Multipliers boosted' : 'Multipliers reduced'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Target New Intake</p>
                        <p className="text-2xl font-bold text-blue-600">{formatCurrency(forecastSummary.target_new_intake)}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {forecastSummary.carryover_below_threshold ? 'Total capped at budget' : 
                           forecastSummary.target_new_intake <= forecastSummary.budget ? 'Capped at budget' : 'Above budget'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Base Forecast</p>
                        <p className="text-2xl font-bold text-gray-700">{formatCurrency(forecastSummary.base_forecast)}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          New intake projection
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-white rounded-lg border border-purple-200">
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">Logic:</span> 
                        {forecastSummary.carryover_below_threshold && forecastSummary.days_with_data === 0 ? (
                          <span className="text-amber-600 ml-2">
                            Carryover ({formatCurrency(forecastSummary.carryover)}) &lt; £400k threshold 
                            → Total forecast capped at budget
                          </span>
                        ) : forecastSummary.performance_ratio < 1.0 ? (
                          <span className="text-amber-600 ml-2">
                            Avg intake ({formatCurrency(forecastSummary.average_daily_intake)}) &lt; Baseline ({formatCurrency(forecastSummary.baseline_per_day)}) 
                            → Projection capped at budget
                          </span>
                        ) : (
                          <span className="text-emerald-600 ml-2">
                            Avg intake ({formatCurrency(forecastSummary.average_daily_intake)}) ≥ Baseline ({formatCurrency(forecastSummary.baseline_per_day)}) 
                            → Projection allowed above budget
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Performance Section */}
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg border-2 border-emerald-200">
                    <h4 className="font-semibold text-lg mb-3 text-emerald-800">Performance Analysis</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Days with Data</p>
                        <p className="text-2xl font-bold text-emerald-600">{forecastSummary.days_with_data}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Days Remaining</p>
                        <p className="text-2xl font-bold text-amber-600">{forecastSummary.days_remaining}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Total Actuals</p>
                        <p className="text-2xl font-bold text-emerald-600">{formatCurrency(forecastSummary.total_actuals)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Avg Daily Intake</p>
                        <p className="text-2xl font-bold text-blue-600">{formatCurrency(forecastSummary.average_daily_intake)}</p>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-white rounded-lg">
                      <p className="text-sm text-gray-600">
                        Performance vs Baseline: 
                        <span className={`font-bold ml-2 ${
                          forecastSummary.performance_vs_baseline >= 100 ? 'text-emerald-600' : 
                          forecastSummary.performance_vs_baseline >= 90 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {forecastSummary.performance_vs_baseline?.toFixed(1)}%
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Projection Section */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border-2 border-blue-200">
                    <h4 className="font-semibold text-lg mb-3 text-blue-800">Projection Calculation</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                        <span className="text-gray-600">Total Actuals (days with data)</span>
                        <span className="font-bold text-emerald-600">{formatCurrency(forecastSummary.total_actuals)}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                        <span className="text-gray-600">+ Projected Remaining (adjusted multipliers × {forecastSummary.adjustment_factor?.toFixed(2)})</span>
                        <span className="font-bold text-blue-600">{formatCurrency(forecastSummary.projected_remaining)}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                        <span className="text-gray-600">= Base Forecast (new intake)</span>
                        <span className="font-bold text-gray-700">{formatCurrency(forecastSummary.base_forecast)}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <span className="text-blue-700">+ Carry Over (starting balance)</span>
                        <span className="font-bold text-blue-600">{formatCurrency(forecastSummary.carryover)}</span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-lg border-2 border-emerald-300">
                        <span className="font-bold text-emerald-800 text-lg">= FORECAST EOM</span>
                        <span className="font-bold text-emerald-700 text-2xl">{formatCurrency(forecastSummary.forecast_eom)}</span>
                      </div>
                    </div>
                  </div>

                  {/* SPWD & Variance */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-r from-cyan-50 to-sky-50 p-4 rounded-lg border-2 border-cyan-200">
                      <h4 className="font-semibold mb-2 text-cyan-800">SPWD Forecast</h4>
                      <p className="text-3xl font-bold text-cyan-700">{formatCurrency(forecastSummary.spwd_forecast)}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        = {formatCurrency(forecastSummary.forecast_eom)} ÷ {forecastSummary.working_days} days
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg border-2 ${
                      forecastSummary.forecast_status === 'on_track' ? 'bg-emerald-50 border-emerald-200' :
                      forecastSummary.forecast_status === 'over_budget' ? 'bg-green-50 border-green-200' :
                      'bg-red-50 border-red-200'
                    }`}>
                      <h4 className="font-semibold mb-2 text-gray-800">Variance from Budget</h4>
                      <p className={`text-3xl font-bold ${
                        forecastSummary.variance_from_budget >= 0 ? 'text-emerald-600' : 'text-red-600'
                      }`}>
                        {forecastSummary.variance_from_budget >= 0 ? '+' : ''}{formatCurrency(forecastSummary.variance_from_budget)}
                      </p>
                      <p className={`text-sm font-semibold mt-2 ${
                        forecastSummary.forecast_status === 'on_track' ? 'text-emerald-600' :
                        forecastSummary.forecast_status === 'over_budget' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {forecastSummary.variance_percentage >= 0 ? '+' : ''}{forecastSummary.variance_percentage.toFixed(1)}% 
                        ({forecastSummary.forecast_status === 'on_track' ? '✓ On Track' :
                          forecastSummary.forecast_status === 'over_budget' ? '↑ Over Budget' : '↓ Under Budget'})
                      </p>
                    </div>
                  </div>

                  {/* How It Works */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h5 className="font-semibold text-sm mb-2 text-gray-700">HOW THE FORECAST WORKS:</h5>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li>• <span className="text-emerald-600 font-bold">Actual Data</span>: Uses real order intake from uploaded figures</li>
                      <li>• <span className="text-blue-600 font-bold">Day-of-Week Weighting</span>: Mon/Tue/Wed higher multipliers than Thu/Fri</li>
                      <li>• <span className="text-purple-600 font-bold">Week Weighting</span>: Week 1 slightly higher than Week 2</li>
                      <li>• <span className="text-amber-600 font-bold">End-of-Month Decline</span>: Last 10 days reduce from 0.95× to 0× (last day)</li>
                      <li>• <span className="text-emerald-600 font-bold">Carry Over Boost</span>: &gt;£400k = +3%, &gt;£500k = +8%, &gt;£600k = +15%</li>
                      <li>• <span className="text-cyan-600 font-bold">SPWD Forecast</span>: Forecast EOM ÷ Working Days</li>
                      <li>• <span className="text-gray-600 font-bold">No Data = Budget</span>: Multipliers calibrated so start-of-month forecast equals budget</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">Loading forecast data...</p>
              )}
            </TabsContent>
          </Tabs>
          
          {/* Refresh Button */}
          <div className="mt-4 flex justify-end">
            <Button 
              onClick={() => {
                loadWeights();
                loadEntries();
                toast.success('Forecast recalculated');
              }}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Forecast
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Modal */}
      <Dialog open={manualEntryModalOpen} onOpenChange={setManualEntryModalOpen}>
        <DialogContent className="max-w-[90vw] w-full max-h-[80vh] overflow-hidden flex flex-col mt-[5vh] top-[5vh]">
          <div 
            className="flex-1 overflow-y-auto p-6 rounded-lg"
            style={{
              background: 'linear-gradient(to right, #fff5f0, #ffe0f0)',
              border: '4px solid transparent',
              backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #fb923c, #f472b6)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
            }}
          >
            <DialogTitle className="text-2xl font-bold text-orange-600 mb-4">Manual Entry Editor</DialogTitle>
            
            <div className="bg-white rounded-lg p-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                {/* Header */}
                <div className="grid grid-cols-8 gap-2 px-4 py-2 bg-gray-100 rounded-lg font-semibold text-xs sticky top-0 z-10">
                  <div>Date</div>
                  <div>WD</div>
                  <div>Invoiced</div>
                  <div>Daily Invoiced</div>
                  <div>Outstanding</div>
                  <div>Total Sales</div>
                  <div>Contrib %</div>
                  <div>Actions</div>
                </div>

                {/* Editable Rows */}
                {editableEntries.map((entry) => (
                  <div 
                    key={entry.id} 
                    className="grid grid-cols-8 gap-2 px-4 py-3 bg-white border rounded-lg hover:bg-gray-50 text-sm items-center"
                  >
                    <div className="font-medium text-xs">
                      {new Date(entry.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    </div>
                    <div className="text-gray-600">{entry.working_day}</div>
                    
                    <Input
                      type="number"
                      value={entry.invoiced || ''}
                      onChange={(e) => {
                        console.log(`🔵 Changed invoiced for ${entry.id}:`, e.target.value);
                        setEditableEntries(prev => prev.map(ent => 
                          ent.id === entry.id ? { ...ent, invoiced: e.target.value } : ent
                        ));
                      }}
                      className="h-8 text-xs"
                      placeholder="0"
                    />
                    
                    <Input
                      type="number"
                      value={entry.daily_invoiced || ''}
                      onChange={(e) => {
                        console.log(`🔵 Changed daily_invoiced for ${entry.id}:`, e.target.value);
                        setEditableEntries(prev => prev.map(ent => 
                          ent.id === entry.id ? { ...ent, daily_invoiced: e.target.value } : ent
                        ));
                      }}
                      className="h-8 text-xs"
                      placeholder="0"
                    />
                    
                    <Input
                      type="number"
                      value={entry.outstanding || ''}
                      onChange={(e) => {
                        console.log(`🔵 Changed outstanding for ${entry.id}:`, e.target.value);
                        setEditableEntries(prev => prev.map(ent => 
                          ent.id === entry.id ? { ...ent, outstanding: e.target.value } : ent
                        ));
                      }}
                      className="h-8 text-xs"
                      placeholder="0"
                    />
                    
                    <Input
                      type="number"
                      value={entry.forecasted_sales || entry.forecasted_sales_value || ''}
                      onChange={(e) => {
                        console.log(`🔵 Changed forecasted_sales for ${entry.id}:`, e.target.value);
                        setEditableEntries(prev => prev.map(ent => 
                          ent.id === entry.id ? { ...ent, forecasted_sales: e.target.value, forecasted_sales_value: e.target.value } : ent
                        ));
                      }}
                      className="h-8 text-xs"
                      placeholder="0"
                    />
                    
                    <Input
                      type="number"
                      value={entry.contribution_percentage || ''}
                      onChange={(e) => {
                        console.log(`🔵 Changed contribution_percentage for ${entry.id}:`, e.target.value);
                        setEditableEntries(prev => prev.map(ent => 
                          ent.id === entry.id ? { ...ent, contribution_percentage: e.target.value } : ent
                        ));
                      }}
                      className="h-8 text-xs"
                    />
                    
                    <Button
                      onClick={() => clearEntryData(entry.id)}
                      variant="outline"
                      size="sm"
                      className="h-8 text-orange-600 border-orange-300 hover:bg-orange-50"
                      title="Clear all data for this day"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Clear
                    </Button>
                  </div>
                ))}
              </div>
              
              {/* Save Button */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <Button
                  onClick={() => setManualEntryModalOpen(false)}
                  variant="outline"
                  disabled={savingEntries}
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveAllEntries}
                  disabled={savingEntries}
                  className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white"
                >
                  {savingEntries ? 'Saving...' : 'Save All & Close'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
    </>
  );
};

export default SalesTracker;
